/**
 * HR attendance API client (Phase 4). Module-level typed async fns over the
 * `api` singleton. Precedent: lib/hr-leave-api.ts. Every endpoint is
 * house-enveloped ({success, data, message?}) — unwrap `response.data.data`.
 *
 * `api` already prefixes /api/v1.
 */

import api from "./api"
import { buildQueryParams } from "./utils/query-params"
import type { ApiResponse } from "@/types"
import type {
  AttendanceTodayState,
  AttendanceDayData,
  AttendanceDayRow,
  AttendanceSheet,
  AttendanceDetail,
  CorrectAttendanceDayData,
  RederiveResult,
  SelfCheckinData,
} from "./hr-types"

// ── Self check-in (hr.attendance.self) ───────────────────────────────────────

/**
 * POST /hr/attendance/checkins/self — punch in / out. Omit `direction` to let
 * the server infer the toggle. Returns the caller's refreshed today-state.
 */
export const selfCheckin = async (
  direction?: "in" | "out",
): Promise<AttendanceTodayState> => {
  const body: SelfCheckinData = { direction }
  const response = await api.post<ApiResponse<AttendanceTodayState>>(
    `/hr/attendance/checkins/self`,
    body,
  )
  return response.data.data
}

/** GET /hr/attendance/checkins/today — the caller's own today-state. */
export const getTodayCheckins = async (): Promise<AttendanceTodayState> => {
  const response = await api.get<ApiResponse<AttendanceTodayState>>(
    `/hr/attendance/checkins/today`,
  )
  return response.data.data
}

// ── Admin day view + month sheet (hr.attendance.view) ─────────────────────────

/**
 * GET /hr/attendance/day?date=YYYY-MM-DD — one day's roster of attendance.
 * The envelope's data is {date, rows} (NOT a bare array) — unwrap `.rows`;
 * the day table owns the date locally so the echo isn't consumed.
 */
export const getAttendanceDay = async (
  date: string,
): Promise<AttendanceDayRow[]> => {
  const query = buildQueryParams({ date })
  const response = await api.get<ApiResponse<AttendanceDayData>>(
    `/hr/attendance/day?${query}`,
  )
  return response.data.data.rows
}

/**
 * GET /hr/attendance/sheet?month=YYYY-MM — the month letter grid + totals.
 * Optional `userId` narrows to one staff member; for callers holding only
 * hr.attendance.self the server transparently self-scopes to their own row
 * regardless (the self-only /hr/attendance view relies on this).
 */
export const getAttendanceSheet = async (
  month: string,
  userId?: number,
): Promise<AttendanceSheet> => {
  const query = buildQueryParams({ month, user_id: userId })
  const response = await api.get<ApiResponse<AttendanceSheet>>(
    `/hr/attendance/sheet?${query}`,
  )
  return response.data.data
}

// ── Corrections + re-derivation (hr.attendance.manage) ────────────────────────

/**
 * PUT /hr/attendance/days/{userId}/{date} — save a manual correction. `date`
 * is YYYY-MM-DD. The corrected day comes back with source='manual'; callers
 * refetch the day/sheet after saving, so the return value isn't consumed.
 */
export const correctAttendanceDay = async (
  userId: number,
  date: string,
  data: CorrectAttendanceDayData,
): Promise<AttendanceDetail> => {
  const response = await api.put<ApiResponse<AttendanceDetail>>(
    `/hr/attendance/days/${userId}/${date}`,
    data,
  )
  return response.data.data
}

/**
 * POST /hr/attendance/rederive — recompute derived rows for a date. Manually
 * corrected days are left untouched; returns the affected counts.
 */
export const rederiveAttendance = async (
  date: string,
): Promise<RederiveResult> => {
  const response = await api.post<ApiResponse<RederiveResult>>(
    `/hr/attendance/rederive`,
    { date },
  )
  return response.data.data
}
